<!-- src/components/ui/tags-input/TagsInput.vue -->
<!-- shadcn-vue style TagsInput component for multiple location selection -->
<!-- Supports adding/removing tags with keyboard and mouse interaction -->
<!-- RELEVANT FILES: src/components/ui/input/Input.vue, src/components/ui/badge/Badge.vue -->

<script setup lang="ts">
import { X } from 'lucide-vue-next';
import { computed, ref } from 'vue';

import { cn } from '@/lib/utils';

interface TagsInputProps {
  modelValue?: string[];
  placeholder?: string;
  disabled?: boolean;
  maxTags?: number;
  class?: string;
  // Allow validation of individual tags
  validateTag?: (tag: string) => boolean;
  // Custom separator (default: comma, enter)
  separator?: string | RegExp;
}

interface TagsInputEmits {
  'update:modelValue': [value: string[]];
  tagAdd: [tag: string];
  tagRemove: [tag: string];
}

const props = withDefaults(defineProps<TagsInputProps>(), {
  modelValue: () => [],
  placeholder: 'Add tags...',
  disabled: false,
  maxTags: undefined,
  validateTag: () => true,
  separator: () => /[,\n]/,
});

const emit = defineEmits<TagsInputEmits>();

const inputRef = ref<HTMLInputElement>();
const inputValue = ref('');
const focused = ref(false);

const tags = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const canAddMore = computed(() => {
  return !props.maxTags || tags.value.length < props.maxTags;
});

function addTag(tagText: string) {
  const trimmed = tagText.trim();

  if (!trimmed) {return;}
  if (tags.value.includes(trimmed)) {return;}
  if (!props.validateTag(trimmed)) {return;}
  if (!canAddMore.value) {return;}

  const newTags = [...tags.value, trimmed];
  tags.value = newTags;
  emit('tagAdd', trimmed);
  inputValue.value = '';
}

function removeTag(index: number) {
  const removedTag = tags.value[index];
  const newTags = tags.value.filter((_, i) => i !== index);
  tags.value = newTags;
  emit('tagRemove', removedTag);
}

function removeLastTag() {
  if (tags.value.length > 0 && inputValue.value === '') {
    removeTag(tags.value.length - 1);
  }
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Enter' || event.key === ',') {
    event.preventDefault();
    addTag(inputValue.value);
  } else if (event.key === 'Backspace') {
    if (inputValue.value === '') {
      removeLastTag();
    }
  }
}

function handleInputBlur() {
  focused.value = false;
  // Add remaining input as tag on blur
  if (inputValue.value.trim()) {
    addTag(inputValue.value);
  }
}

function handlePaste(event: ClipboardEvent) {
  event.preventDefault();
  const paste = event.clipboardData?.getData('text') || '';
  if (!paste) {return;}

  // Split by separator and collect all valid tags
  const pastedTags = paste
    .split(props.separator)
    .map(tag => tag.trim())
    .filter(Boolean);

  // Use a Set for efficient checking of existing tags
  const existingTags = new Set(tags.value);

  const tagsToAdd: string[] = [];
  for (const tag of pastedTags) {
    // Stop if the tag limit is reached
    if (props.maxTags && existingTags.size + tagsToAdd.length >= props.maxTags) {
      break;
    }
    // Check for validity and uniqueness against both existing and newly pasted tags
    if (props.validateTag(tag) && !existingTags.has(tag) && !tagsToAdd.includes(tag)) {
      tagsToAdd.push(tag);
    }
  }

  if (tagsToAdd.length === 0) {
    return;
  }

  // Update the v-model once with the new, complete array of tags
  tags.value = [...tags.value, ...tagsToAdd];

  // Emit an event for each tag that was successfully added
  tagsToAdd.forEach(tag => emit('tagAdd', tag));

  inputValue.value = '';
}

function focusInput() {
  if (!props.disabled) {
    inputRef.value?.focus();
    focused.value = true;
  }
}
</script>

<template>
  <div
    :class="
      cn(
        'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
        {
          'cursor-text': !disabled,
        },
        props.class,
      )
    "
    @click="focusInput"
  >
    <!-- Render existing tags -->
    <div
      v-for="(tag, index) in tags"
      :key="`${tag}-${index}`"
      class="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground"
    >
      <span>{{ tag }}</span>
      <button
        v-if="!disabled"
        type="button"
        class="ml-1 rounded-full p-0.5 hover:bg-secondary-foreground/20 focus:bg-secondary-foreground/20 focus:outline-none"
        @click.stop="removeTag(index)"
      >
        <X class="h-3 w-3" />
        <span class="sr-only">Remove {{ tag }}</span>
      </button>
    </div>

    <!-- Input for new tags -->
    <input
      v-if="canAddMore"
      ref="inputRef"
      v-model="inputValue"
      type="text"
      :placeholder="tags.length === 0 ? placeholder : ''"
      :disabled="disabled"
      class="flex-1 bg-transparent outline-none placeholder:text-muted-foreground min-w-[120px]"
      @keydown="handleKeyDown"
      @blur="handleInputBlur"
      @focus="focused = true"
      @paste="handlePaste"
    />

    <!-- Max tags reached indicator -->
    <span v-if="!canAddMore" class="text-xs text-muted-foreground"> Max {{ maxTags }} tags </span>
  </div>
</template>
